/**
 * @fileoverview React Query hooks for room management operations
 * @author Hospital Room Scheduler Team
 * @created 2025-01-08
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import {
  getRooms,
  getRoomById,
  createRoom,
  updateRoom,
  deleteRoom,
  getRoomsByFloor,
  getRoomStats,
  getRoomsForSelection,
  getUnpositionedRooms,
  createRoomWithPosition,
  type GetRoomsParams,
  type CreateRoomData,
  type UpdateRoomData,
  type GetRoomStatsParams,
  type RoomWithDetails,
  type RoomStats,
  type CreateRoomWithPositionData
} from '../../api/endpoints/rooms';

/**
 * Query keys for room-related queries
 */
export const roomQueryKeys = {
  all: ['rooms'] as const,
  lists: () => [...roomQueryKeys.all, 'list'] as const,
  list: (params: GetRoomsParams) => [...roomQueryKeys.lists(), params] as const,
  details: () => [...roomQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...roomQueryKeys.details(), id] as const,
  byFloor: (floorId: string) => [...roomQueryKeys.all, 'byFloor', floorId] as const,
  stats: (params: GetRoomStatsParams) => [...roomQueryKeys.all, 'stats', params] as const,
  selection: () => [...roomQueryKeys.all, 'selection'] as const,
  unpositioned: (floorId: string) => [...roomQueryKeys.all, 'unpositioned', floorId] as const,
};

/**
 * Hook to fetch paginated rooms with filtering and sorting
 * @param params - Query parameters for filtering and pagination
 * @returns React Query result with rooms data
 */
export const useRooms = (params: GetRoomsParams = {}) => {
  return useQuery({
    queryKey: roomQueryKeys.list(params),
    queryFn: () => getRooms(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

/**
 * Hook to fetch a single room by ID
 * @param id - Room ID
 * @param enabled - Whether the query should be enabled
 * @returns React Query result with room data
 */
export const useRoomById = (id: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: roomQueryKeys.detail(id),
    queryFn: () => getRoomById(id),
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

/**
 * Hook to create a new room
 * @returns Mutation for creating a room
 */
export const useCreateRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRoomData) => {
      return createRoom(data);
    },
    onSuccess: (_data, variables) => {
      // Invalidate and refetch room lists
      queryClient.invalidateQueries({ queryKey: roomQueryKeys.lists() });
      
      // Invalidate stats if building/floor is specified
      if (variables.floorId) {
        queryClient.invalidateQueries({ queryKey: roomQueryKeys.byFloor(variables.floorId) });
      }
      
      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: [...roomQueryKeys.all, 'stats'] });
    },
    onError: (error: any) => {
      console.error('Failed to create room:', error);
    },
  });
};

/**
 * Hook to update an existing room
 * @returns Mutation for updating a room
 */
export const useUpdateRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoomData }) => {
      return updateRoom(id, data);
    },
    onSuccess: (data, variables) => {
      // Update the specific room in cache
      queryClient.setQueryData(roomQueryKeys.detail(variables.id), data);
      
      // Invalidate and refetch room lists
      queryClient.invalidateQueries({ queryKey: roomQueryKeys.lists() });
      
      // Invalidate by floor queries
      queryClient.invalidateQueries({ queryKey: [...roomQueryKeys.all, 'byFloor'] });
      
      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: [...roomQueryKeys.all, 'stats'] });
    },
    onError: (error: any) => {
      console.error('Failed to update room:', error);
    },
  });
};

/**
 * Hook to delete a room
 * @returns Mutation for deleting a room
 */
export const useDeleteRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      return deleteRoom(id);
    },
    onSuccess: (_data, roomId) => {
      // Remove the room from cache
      queryClient.removeQueries({ queryKey: roomQueryKeys.detail(roomId) });
      
      // Invalidate and refetch room lists
      queryClient.invalidateQueries({ queryKey: roomQueryKeys.lists() });
      
      // Invalidate by floor queries
      queryClient.invalidateQueries({ queryKey: [...roomQueryKeys.all, 'byFloor'] });
      
      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: [...roomQueryKeys.all, 'stats'] });
    },
    onError: (error: any) => {
      console.error('Failed to delete room:', error);
    },
  });
};

/**
 * Hook to fetch rooms by floor ID
 * @param floorId - Floor ID
 * @param enabled - Whether the query should be enabled
 * @returns React Query result with rooms data
 */
export const useRoomsByFloor = (floorId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: roomQueryKeys.byFloor(floorId),
    queryFn: () => getRoomsByFloor(floorId),
    enabled: enabled && !!floorId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

/**
 * Hook to fetch room statistics
 * @param params - Query parameters for filtering
 * @returns React Query result with room statistics
 */
export const useRoomStats = (params: GetRoomStatsParams = {}) => {
  return useQuery({
    queryKey: roomQueryKeys.stats(params),
    queryFn: () => getRoomStats(params),
    staleTime: 2 * 60 * 1000, // 2 minutes (stats change more frequently)
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

/**
 * Hook to prefetch room data for better UX
 * @param roomId - Room ID to prefetch
 */
export const usePrefetchRoom = () => {
  const queryClient = useQueryClient();

  return (roomId: string) => {
    queryClient.prefetchQuery({
      queryKey: roomQueryKeys.detail(roomId),
      queryFn: () => getRoomById(roomId),
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };
};

/**
 * Hook to invalidate all room-related queries
 * Useful for refreshing all room data after major changes
 */
export const useInvalidateRooms = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: roomQueryKeys.all });
  };
};

/**
 * Hook to get room data from cache without triggering a fetch
 * @param roomId - Room ID
 * @returns Room data from cache or undefined
 */
export const useRoomFromCache = (roomId: string) => {
  const queryClient = useQueryClient();
  
  return queryClient.getQueryData<RoomWithDetails>(roomQueryKeys.detail(roomId));
};

/**
 * Hook to get rooms list data from cache without triggering a fetch
 * @param params - Query parameters
 * @returns Rooms list data from cache or undefined
 */
export const useRoomsFromCache = (params: GetRoomsParams = {}) => {
  const queryClient = useQueryClient();
  
  return queryClient.getQueryData(roomQueryKeys.list(params));
};

/**
 * Hook to get room statistics from cache without triggering a fetch
 * @param params - Query parameters
 * @returns Room statistics from cache or undefined
 */
export const useRoomStatsFromCache = (params: GetRoomStatsParams = {}) => {
  const queryClient = useQueryClient();
  
  return queryClient.getQueryData<RoomStats>(roomQueryKeys.stats(params));
};

/**
 * Hook to get rooms for selection/dropdown
 * @returns Rooms selection query result
 */
export const useRoomsForSelection = () => {
  return useQuery({
    queryKey: roomQueryKeys.selection(),
    queryFn: () => getRoomsForSelection(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

/**
 * Hook to fetch unpositioned rooms for a specific floor
 * @param floorId - Floor ID
 * @returns React Query result with unpositioned rooms data
 * @created 15-10-2025 23:10:00
 */
export const useUnpositionedRooms = (floorId: string) => {
  return useQuery({
    queryKey: roomQueryKeys.unpositioned(floorId),
    queryFn: () => getUnpositionedRooms(floorId),
    enabled: !!floorId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

/**
 * Hook to create room with position in atomic transaction
 * @returns Mutation for creating room with position
 * @created 15-10-2025 23:12:00
 */
export const useCreateRoomWithPosition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRoomWithPositionData) => createRoomWithPosition(data),
    onSuccess: async (_data, variables) => {
      // Invalidate all queries related to the floor
      await Promise.all([
        // Unpositioned rooms (one less now)
        queryClient.invalidateQueries({ 
          queryKey: roomQueryKeys.unpositioned(variables.floorId) 
        }),
        // Floor plan data (new room on canvas) - matches useFloorPlanData query key
        queryClient.invalidateQueries({ 
          queryKey: ['floorPlan']  // Invalidates all floorPlan queries
        }),
        // Room layouts for the floor
        queryClient.invalidateQueries({ 
          queryKey: ['room-layouts', variables.floorId] 
        }),
        // Rooms list
        queryClient.invalidateQueries({ 
          queryKey: roomQueryKeys.lists() 
        }),
        // Rooms by floor
        queryClient.invalidateQueries({ 
          queryKey: roomQueryKeys.byFloor(variables.floorId) 
        })
      ]);
      
      notifications.show({
        title: 'Room Created',
        message: `Room ${variables.room.number} created and positioned successfully`,
        color: 'green',
        autoClose: 3000
      });
    },
    onError: (error: any) => {
      console.error('Room creation error:', error);
      
      // Extract error message from API response
      const errorMessage = error.message || error.error?.message || 'Failed to create room with position';
      
      // Show user-friendly notification based on error type
      if (errorMessage.includes('already exists') || errorMessage.includes('duplicate')) {
        notifications.show({
          title: 'Duplicate Room Number',
          message: errorMessage,
          color: 'orange',
          autoClose: 5000
        });
      } else if (errorMessage.includes('not found')) {
        notifications.show({
          title: 'Floor Not Found',
          message: 'The selected floor does not exist. Please refresh and try again.',
          color: 'red',
          autoClose: 5000
        });
      } else if (errorMessage.includes('inactive')) {
        notifications.show({
          title: 'Floor Inactive',
          message: 'The selected floor is inactive. Please select an active floor.',
          color: 'yellow',
          autoClose: 5000
        });
      } else {
        notifications.show({
          title: 'Creation Failed',
          message: errorMessage,
          color: 'red',
          autoClose: 5000
        });
      }
    }
  });
};
